"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listReactiveExpressions = listReactiveExpressions;
const errors_1 = require("../errors");
const type_validation_1 = require("../utils/type-validation");
const extract_dependencies_1 = require("./extract-dependencies");
function* createExpressionsFromTerm(term, iterable, compatibility) {
    if (term.calculate) {
        if (term.calculate.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the calculate expression for term "${term.name}"`);
        }
        const dependencies = (0, extract_dependencies_1.extractDependencies)(term.calculate.tree, {
            term,
            iterable,
        });
        const exp = {
            type: "basic",
            name: "calculate",
            termName: term.name,
            iterableName: iterable?.name,
            syntax: term.calculate.tree,
            dependencies,
            iterableInstanceDeps: iterableInstanceDepsFromRefLocationNodes(dependencies),
        };
        yield exp;
    }
    if (term.label && term.label.type !== "text") {
        if (term.label.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the label expression for term "${term.name}"`);
        }
        const dependencies = (0, extract_dependencies_1.extractDependencies)(term.label.tree, {
            term,
            iterable,
        });
        const exp = {
            type: "basic",
            name: "label",
            termName: term.name,
            iterableName: iterable?.name,
            syntax: term.label.tree,
            dependencies,
            iterableInstanceDeps: iterableInstanceDepsFromRefLocationNodes(dependencies),
        };
        yield exp;
    }
    if (term.relevant) {
        if (term.relevant.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the relevant expression for term "${term.name}"`);
        }
        const dependencies = (0, extract_dependencies_1.extractDependencies)(term.relevant.tree, {
            term,
            iterable,
        });
        const exp = {
            type: "basic",
            name: "relevant",
            termName: term.name,
            iterableName: iterable?.name,
            syntax: term.relevant.tree,
            dependencies,
            iterableInstanceDeps: iterableInstanceDepsFromRefLocationNodes(dependencies),
        };
        yield exp;
    }
    if (term.required) {
        if (term.required.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the required expression for term "${term.name}"`);
        }
        const dependencies = (0, extract_dependencies_1.extractDependencies)(term.required.tree, {
            term,
            iterable,
        });
        const exp = {
            type: "basic",
            name: "required",
            termName: term.name,
            iterableName: iterable?.name,
            syntax: term.required.tree,
            dependencies,
            iterableInstanceDeps: {},
        };
        // A term can only be required if it is relevant
        // so relevant is always a dependency of required if it exists
        if (term.relevant) {
            exp.dependencies.push(iterable
                ? {
                    type: "iterable-term",
                    iterableName: iterable.name,
                    iterableIndex: -1,
                    termName: term.name,
                    property: "isRelevant",
                }
                : {
                    type: "term",
                    termName: term.name,
                    property: "isRelevant",
                });
        }
        exp.iterableInstanceDeps = iterableInstanceDepsFromRefLocationNodes(exp.dependencies);
        yield exp;
    }
    if (term.readonly) {
        if (term.readonly.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the readonly expression for term "${term.name}"`);
        }
        const dependencies = (0, extract_dependencies_1.extractDependencies)(term.readonly.tree, {
            term,
            iterable,
        });
        const exp = {
            type: "basic",
            name: "readonly",
            termName: term.name,
            iterableName: iterable?.name,
            syntax: term.readonly.tree,
            dependencies,
            iterableInstanceDeps: iterableInstanceDepsFromRefLocationNodes(dependencies),
        };
        yield exp;
    }
    if (term.validationList ||
        term.enums ||
        term.enumsFn ||
        (term.valueType &&
            (type_validation_1.typeValidationMap[term.valueType] || term.valueType === "date"))) {
        const exp = {
            type: "validation",
            termName: term.name,
            iterableName: iterable?.name,
            validators: [],
            valueType: term.valueType || "string",
            dependencies: [
                iterable
                    ? {
                        type: "iterable-term",
                        iterableName: iterable.name,
                        iterableIndex: -1,
                        termName: term.name,
                        property: "value",
                    }
                    : {
                        type: "term",
                        termName: term.name,
                        property: "value",
                    },
                iterable
                    ? {
                        type: "iterable-term",
                        iterableName: iterable.name,
                        iterableIndex: -1,
                        termName: term.name,
                        property: "enums",
                    }
                    : {
                        type: "term",
                        termName: term.name,
                        property: "enums",
                    },
            ],
            iterableInstanceDeps: {},
        };
        if (term.validationList) {
            for (const validation of term.validationList) {
                if (validation.min) {
                    if (validation.min.type === "syntax") {
                        throw new Error(`There is no syntax-tree for the validation value expression for term "${term.name}"`);
                    }
                    exp.validators.push({
                        type: mapValidatorTypes(validation.validationType, "min"),
                        syntax: validation.min.type === "text"
                            ? {
                                type: "string",
                                value: validation.min.value,
                                text: validation.min.value,
                            }
                            : validation.min.tree,
                    });
                    if (validation.min.type === "syntax-tree") {
                        exp.dependencies.push(...(0, extract_dependencies_1.extractDependencies)(validation.min.tree, { term, iterable }));
                    }
                }
                if (validation.max) {
                    if (validation.max.type === "syntax") {
                        throw new Error(`There is no syntax-tree for the validation value expression for term "${term.name}"`);
                    }
                    exp.validators.push({
                        type: mapValidatorTypes(validation.validationType, "max"),
                        syntax: validation.max.type === "text"
                            ? {
                                type: "string",
                                value: validation.max.value,
                                text: validation.max.value,
                            }
                            : validation.max.tree,
                    });
                    if (validation.max.type === "syntax-tree") {
                        exp.dependencies.push(...(0, extract_dependencies_1.extractDependencies)(validation.max.tree, { term, iterable }));
                    }
                }
            }
        }
        exp.iterableInstanceDeps = iterableInstanceDepsFromRefLocationNodes(exp.dependencies);
        yield exp;
    }
    if (term.enums?.some(({ relevant }) => relevant)) {
        const exp = {
            type: "enums-relevant",
            termName: term.name,
            iterableName: iterable?.name,
            items: [],
            dependencies: [],
            iterableInstanceDeps: {},
        };
        for (const [index, enumItem] of term.enums.entries()) {
            if (enumItem.relevant) {
                if (enumItem.relevant.type !== "syntax-tree") {
                    throw new Error(`There is no syntax for the enum relevant expression for term "${term.name}"`);
                }
                exp.items.push({
                    syntax: enumItem.relevant.tree,
                    enumIndex: index,
                });
                exp.dependencies.push(...(0, extract_dependencies_1.extractDependencies)(enumItem.relevant.tree, {
                    term,
                    iterable,
                }));
            }
        }
        exp.iterableInstanceDeps = iterableInstanceDepsFromRefLocationNodes(exp.dependencies);
        yield exp;
    }
    if (term.enumsFn && compatibility?.dynamicEnums) {
        if (term.enumsFn.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the enums expression for term "${term.name}"`);
        }
        const dependencies = [
            iterable
                ? {
                    type: "iterable-term",
                    iterableName: iterable.name,
                    iterableIndex: -1,
                    termName: term.name,
                    property: "isRelevant",
                }
                : {
                    type: "term",
                    termName: term.name,
                    property: "isRelevant",
                },
            ...(0, extract_dependencies_1.extractDependencies)(term.enumsFn.tree, {
                term,
                iterable,
            }),
        ];
        const exp = {
            type: "enums-fn",
            termName: term.name,
            iterableName: iterable?.name,
            syntax: term.enumsFn.tree,
            initialEnumsLength: term.enums?.length || 0,
            dependencies,
            iterableInstanceDeps: iterableInstanceDepsFromRefLocationNodes(dependencies),
        };
        yield exp;
    }
}
function listReactiveExpressions(formModel) {
    const list = [];
    const reference = {
        terms: {},
        iterables: {},
    };
    for (const term of Object.values(formModel.terms)) {
        for (const exp of createExpressionsFromTerm(term, undefined, formModel.compatibility)) {
            throwIfSelfReferencingDependencies(exp);
            if (!reference.terms[term.name]) {
                reference.terms[term.name] = {};
            }
            if (exp.type === "basic") {
                reference.terms[term.name][exp.name] = exp;
            }
            if (exp.type === "validation") {
                reference.terms[term.name].validation = exp;
            }
            if (exp.type === "enums-relevant") {
                reference.terms[term.name].enumsRelevant = exp;
            }
            if (exp.type === "enums-fn") {
                reference.terms[term.name].enumsFn = exp;
            }
            list.push(exp);
        }
    }
    for (const iterable of Object.values(formModel.iterables)) {
        if (!reference.iterables[iterable.name]) {
            if (iterable.count.type !== "syntax-tree") {
                throw new Error(`There is no syntax for the count expression for iterable "${iterable.name}"`);
            }
            const dependencies = (0, extract_dependencies_1.extractDependencies)(iterable.count.tree, {
                iterable,
            });
            const countExp = {
                type: "count",
                iterableName: iterable.name,
                syntax: iterable.count.tree,
                dependencies,
                iterableInstanceDeps: iterableInstanceDepsFromRefLocationNodes(dependencies),
            };
            throwIfSelfReferencingDependencies(countExp);
            reference.iterables[iterable.name] = {
                count: countExp,
                terms: {},
            };
            list.push(countExp);
        }
        for (const term of Object.values(iterable.terms)) {
            for (const exp of createExpressionsFromTerm(term, iterable, formModel.compatibility)) {
                throwIfSelfReferencingDependencies(exp);
                if (!reference.iterables[iterable.name].terms[term.name]) {
                    reference.iterables[iterable.name].terms[term.name] = {};
                }
                if (exp.type === "basic") {
                    reference.iterables[iterable.name].terms[term.name][exp.name] = exp;
                }
                if (exp.type === "validation") {
                    reference.iterables[iterable.name].terms[term.name].validation = exp;
                }
                if (exp.type === "enums-relevant") {
                    reference.iterables[iterable.name].terms[term.name].enumsRelevant =
                        exp;
                }
                if (exp.type === "enums-fn") {
                    reference.iterables[iterable.name].terms[term.name].enumsFn = exp;
                }
                list.push(exp);
            }
        }
    }
    return [list, reference];
}
function mapValidatorTypes(validationType, minOrMax) {
    switch (validationType) {
        case "amount":
            if (minOrMax === "min") {
                return "amount-min";
            }
            if (minOrMax === "max") {
                return "amount-max";
            }
            throw new Error(`Unknown validation type "${validationType}" > "${minOrMax}"`);
        case "length":
            if (minOrMax === "min") {
                return "length-min";
            }
            if (minOrMax === "max") {
                return "length-max";
            }
            throw new Error(`Unknown validation type "${validationType}" > "${minOrMax}"`);
        case "date-range":
            if (minOrMax === "min") {
                return "date-range-min";
            }
            if (minOrMax === "max") {
                return "date-range-max";
            }
            throw new Error(`Unknown validation type "${validationType}" > "${minOrMax}"`);
        default:
            throw new Error(`Unknown validation type "${validationType}"`);
    }
}
function throwIfSelfReferencingDependencies(exp) {
    for (const dep of exp.dependencies) {
        if ((dep.type === "term" || dep.type === "iterable-term") &&
            (exp.type === "basic" ||
                exp.type === "validation" ||
                exp.type === "enums-fn") &&
            dep.termName === exp.termName &&
            (dep.type !== "iterable-term" || dep.iterableName === exp.iterableName) &&
            ((exp.type === "basic" &&
                doesPropertyMatchExpName(dep.property, exp.name)) ||
                (exp.type === "validation" &&
                    (dep.property === "isValid" || dep.property === "isComplete")) ||
                (exp.type === "enums-fn" &&
                    (dep.property === "enums" ||
                        dep.property === "isValid" ||
                        dep.property === "isComplete")))) {
            throw new errors_1.CyclicalDependencyError(`Self referencing dependency found for term "${exp.iterableName ? exp.iterableName + "/" : ""}${exp.termName}" in "${exp.type === "basic" ? exp.name : exp.type}"`, exp.type === "basic" ? exp.name : exp.type, exp.termName, exp.iterableName);
        }
        if (dep.type === "iterable" &&
            exp.type === "count" &&
            dep.iterableName === exp.iterableName &&
            dep.property === "count") {
            throw new errors_1.CyclicalDependencyError(`Self referencing dependency found for iterable "${exp.iterableName}" in "count"`, "count", undefined, exp.iterableName);
        }
    }
}
function doesPropertyMatchExpName(property, expName) {
    switch (property) {
        case "value":
        case "ready":
        case "hasValue":
        case "isValid":
            return expName === "calculate";
        case "isComplete":
            return (expName === "calculate" ||
                expName === "relevant" ||
                expName === "required");
        case "label":
            return expName === "label";
        case "isRelevant":
            return expName === "relevant";
        case "isRequired":
            return expName === "required" || expName === "relevant";
        case "isReadonly":
            return expName === "readonly";
        default:
            return false;
    }
}
function iterableInstanceDepsFromRefLocationNodes(deps) {
    return deps.reduce((acc, dep) => {
        if (dep.type === "iterable-term") {
            if (acc[dep.iterableName] === "all") {
                return acc;
            }
            acc[dep.iterableName] = dep.iterableIndex === -1 ? "self" : "all";
        }
        return acc;
    }, {});
}
//# sourceMappingURL=list-reactive-expressions.js.map